import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Pagination,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MonetizationOn as MonetizationOnIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useUser } from '../UserContext';
import MoaniDisplay from './MoaniDisplay';

function MoaniTransactions() {
  const { user } = useAuth();
  const { selectUserById } = useUser();
  const navigate = useNavigate();
  
  // State for pagination and filters
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    userId: '',
    type: '',
    context: '',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  
  const [alerts, setAlerts] = useState([]);
  
  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };

  // Fetch moani transactions
  const { data: transactionsData, isLoading, error, refetch } = useQuery({
    queryKey: ['moaniTransactions', page, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });
      
      const response = await api.get(`/moani-transactions?${params}`);
      return response.data;
    },
    enabled: ['manager', 'admin', 'super_admin'].includes(user?.role)
  });

  // Fetch filter options
  const { data: filterOptions } = useQuery({
    queryKey: ['moaniTransactionFilters'],
    queryFn: async () => {
      const response = await api.get('/moani-transactions/filters/options');
      return response.data;
    },
    enabled: ['manager', 'admin', 'super_admin'].includes(user?.role)
  });

  const getTypeColor = (type) => {
    if (!type) return 'default';
    if (type.toLowerCase() === 'add') return 'success';
    if (type.toLowerCase() === 'deduct') return 'error';
    return 'default';
  };

  const getTypeIcon = (type) => {
    if (!type) return null;
    if (type.toLowerCase() === 'add') return <TrendingUpIcon />;
    if (type.toLowerCase() === 'deduct') return <TrendingDownIcon />;
    return null;
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'N/A';
  };

  const handleUserClick = (userId, event) => {
    if (userId && (event.ctrlKey || event.metaKey)) {
      window.open(`/user/${userId}/details`, '_blank');
    } else if (userId) {
      selectUserById(userId, navigate);
    }
    event.preventDefault();
  };

  if (!user || !['manager', 'admin', 'super_admin'].includes(user.role)) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Manager privileges or higher required.</Typography>
      </Box>
    );
  }

  const transactions = transactionsData?.transactions || [];
  const pagination = transactionsData?.pagination || {};
  const summary = transactionsData?.summary || {};

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" component="div" flexGrow={1}>
          MOANI Transaction Log
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Stack>

      {alerts.map(alert => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load transactions: {error.message}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary.byType && summary.byType.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summary.byType.map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.type}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getTypeIcon(item.type)}
                    <Box>
                      <Typography variant="h6">{item.count.toLocaleString()}</Typography>
                      <Typography color="text.secondary" variant="body2">
                        {item.type} Transactions
                      </Typography>
                      {item.totalAmount && (
                        <MoaniDisplay 
                          amount={item.totalAmount} 
                          isWei={true}
                          variant="body2"
                        />
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="User, context, reference..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                label="Type"
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.types?.map((type) => (
                  <MenuItem key={type.type} value={type.type}>
                    {type.type} ({type.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Context</InputLabel>
              <Select
                value={filters.context}
                label="Context"
                onChange={(e) => setFilters(prev => ({ ...prev, context: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {filterOptions?.contexts?.map((context) => (
                  <MenuItem key={context.context} value={context.context}>
                    {context.context} ({context.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => setFilters({ userId: '', type: '', context: '', search: '', dateFrom: '', dateTo: '' })}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Transactions Table */}
      <Paper sx={{ p: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Context</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Reference ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No transactions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(transaction.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {transaction.userId ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={(e) => handleUserClick(transaction.userId, e)}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              <PersonIcon fontSize="small" />
                              {transaction.userName || transaction.userEmail || transaction.userId?.slice(0, 8) || 'Unknown'}
                              <OpenInNewIcon fontSize="small" />
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getTypeIcon(transaction.type)}
                            label={transaction.type || 'Unknown'}
                            size="small"
                            color={getTypeColor(transaction.type)}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.context || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{
                              fontFamily: 'monospace',
                              color: getTypeColor(transaction.type) === 'success' ? 'success.main' : getTypeColor(transaction.type) === 'error' ? 'error.main' : 'text.primary',
                            }}
                          >
                            {transaction.type === 'Add' && '+'}
                            {transaction.type === 'Deduct' && '-'}
                            <MoaniDisplay 
                              amount={transaction.amount} 
                              isWei={true}
                              variant="inherit" 
                              component="span"
                            />
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {transaction.referenceId || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
            
            {/* Results info */}
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" align="center">
                Showing {transactions.length} of {pagination.total?.toLocaleString() || 0} transactions
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default MoaniTransactions;




